/**
 * CharterClasses — professor dashboard for standalone Charter Classes.
 *
 * A Charter Class is a room with `is_charter_only`: teams build a charter and
 * there is no game behind it. The backend keeps the two kinds apart (the rooms
 * list defaults to games), so this screen asks for `?type=charter` explicitly
 * via `fetchCharterClasses`.
 *
 * Deliberately reuses the game dashboard's building blocks — RoomCard,
 * RoomGroup, CreateRoomModal, ConfirmationModal — so the two surfaces stay
 * visually consistent and there is one card implementation to maintain.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GraduationCap, Search } from 'lucide-react'

import { Input } from '@/components/ui/input'
import RoomCard from '@/components/Room/RoomCard'
import { RoomGroup } from '@/components/Room/RoomGroup'
import CreateRoomModal from '@/components/Room/CreateRoomModal'
import ConfirmationModal from '@/components/OGE/OGEDeletionModal'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  createRoom,
  deleteRoom,
  fetchCharterClasses,
} from '@/redux/asyncThunks/room'
import { CardType, ModeType, RoleChoice } from '@/redux/types/room'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import ROUTES from '@/routes/routes'

export default function CharterClasses() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { charterClasses, isLoading } = useAppSelector((state) => state.room)
  const { user } = useAppSelector((state) => state.user)

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [classToDelete, setClassToDelete] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300)
    return () => clearTimeout(t)
  }, [searchQuery])

  const refresh = useCallback(() => {
    dispatch(fetchCharterClasses({ searchQuery: debouncedSearchQuery }))
  }, [dispatch, debouncedSearchQuery])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleCreate = async (className: string) => {
    setIsCreating(true)
    try {
      const created = await dispatch(
        createRoom({
          user: user!,
          roomName: className,
          // A class has no rounds; the backend skips all game scaffolding, so
          // the mode is only a placeholder to satisfy the create serializer.
          timerMode: TimerMode.SINGLE_ROUND,
          isCharterOnly: true,
        })
      ).unwrap()
      setIsCreateOpen(false)
      refresh()
      if (created?.roomid) {
        navigate(ROUTES.PROFESSOR.CHARTER_CLASS(created.roomid))
      }
    } catch (error) {
      console.error('Failed to create charter class:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!classToDelete) return
    try {
      await dispatch(deleteRoom(classToDelete)).unwrap()
      refresh()
    } catch (error) {
      console.error('Failed to delete charter class:', error)
    } finally {
      setClassToDelete(null)
    }
  }

  // A class has no game status to group by, so the only meaningful split is
  // whether the charter work has started (any team placed) or not.
  const groups = useMemo(() => {
    const active = charterClasses.filter((c) => (c.players_count ?? 0) > 0)
    const empty = charterClasses.filter((c) => (c.players_count ?? 0) === 0)
    return { active, empty }
  }, [charterClasses])

  const renderCard = (roomId: string, name: string | null, players?: number) => {
    const klass = charterClasses.find((c) => c.roomid === roomId)
    // Delete is owner-only in RoomCard's menu; co-professors see no Delete.
    // Same ownership derivation as the game dashboard (Room.tsx).
    const isOwner =
      user?.email === klass?.owner || klass?.role === RoleChoice.OWNER
    return (
      <RoomCard
        cardType={CardType.ROOM}
        key={roomId}
        roomId={roomId}
        roomName={name || undefined}
        inviteCode={klass?.invite_code}
        status={klass!.status}
        buttonText='Open Class'
        onButtonClick={() => navigate(ROUTES.PROFESSOR.CHARTER_CLASS(roomId))}
        onDeleteClick={() => setClassToDelete(roomId)}
        isProfessor={true}
        isOwner={isOwner}
        playersCount={players || 0}
        showBadge={false}
      />
    )
  }

  return (
    <div className='p-6'>
      <div className='flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='flex gap-2 items-center text-2xl font-semibold text-dark-blue'>
            <GraduationCap className='w-6 h-6 text-indigo-500' />
            Charter Classes
          </h1>
          <p className='mt-1 text-sm text-gray-500'>
            Teams build a working agreement with the AI coach and moderator. No
            game is attached.
          </p>
        </div>

        <div className='flex gap-3 items-center'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2' />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search classes…'
              className='pl-9 w-64'
            />
          </div>
          <button
            onClick={() => setIsCreateOpen(true)}
            className='px-4 py-2 text-sm font-medium text-white rounded-md bg-dark-blue hover:opacity-90'
          >
            Create Charter Class
          </button>
        </div>
      </div>

      {isLoading && charterClasses.length === 0 ? (
        <p className='text-sm text-gray-500'>Loading classes…</p>
      ) : charterClasses.length === 0 ? (
        <div className='py-16 text-center rounded-lg border border-dashed'>
          <GraduationCap className='mx-auto mb-3 w-10 h-10 text-gray-300' />
          <p className='font-medium text-gray-600'>No charter classes yet</p>
          <p className='mt-1 text-sm text-gray-400'>
            Create one, invite your students, then place them into teams.
          </p>
        </div>
      ) : (
        <>
          <RoomGroup
            title='Active Classes'
            count={groups.active.length}
            dotColor='emerald'
            defaultOpen={true}
          >
            {groups.active.map((c) =>
              renderCard(c.roomid, c.roomname, c.players_count)
            )}
          </RoomGroup>

          <RoomGroup
            title='Awaiting Students'
            count={groups.empty.length}
            dotColor='slate'
            defaultOpen={true}
          >
            {groups.empty.map((c) =>
              renderCard(c.roomid, c.roomname, c.players_count)
            )}
          </RoomGroup>
        </>
      )}

      <CreateRoomModal
        modeType={ModeType.ROOM}
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onConfirm={(name) => handleCreate(name)}
        isLoading={isCreating}
      />

      <ConfirmationModal
        isOpen={!!classToDelete}
        onClose={() => setClassToDelete(null)}
        onConfirm={handleConfirmDelete}
        title='Delete this charter class?'
        description='This permanently deletes the class, its teams and every charter built in it. This cannot be undone.'
        confirmButtonText='Delete class'
      />
    </div>
  )
}
